import React, { useState, useEffect, useRef, useCallback } from "react";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import LeftBar from "./LeftBar";
import RightBar from "./RightBar";
import { useGetLocationBoundsMutation } from "../../features/organisation/organisationApiSlice";
import debounce from "lodash.debounce";
import { useFetchPlannerPatientsMutation } from "../../features/patient/patientApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/auth/authSlice";
import { useFetchPlannerProvidersMutation } from "../../features/provider/providerApiSlice";
import { useFetchPlannerZonesMutation } from "../../features/zones/zonesApiSlice";
import { getCustomMarkerIcon, convertReactIconToUrl } from "./util";
import randomColor from "randomcolor";

const PatientPlannerVisualizer = ({ isScriptLoaded, setIsScriptLoaded }) => {
  const [map, setMap] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [prevBounds, setPrevBounds] = useState(null);

  const [zones, setZones] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);

  const [patients, setPatients] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);

  const [providers, setProviders] = useState([]);
  const [providersList, setProvidersList] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState([]);

  const appliedPatientsRef = useRef([]);
  const appliedProvidersRef = useRef([]);
  const appliedZonesRef = useRef([]);

  const [refetchLocations, setRefetchLocations] = useState(false);

  const markersRef = useRef([]);
  const markerClusterRef = useRef(null);

  const [center, setCenter] = React.useState(null);

  const user = useSelector(selectUser);

  // Add this ref to store provider colors mapping
  const locationProviderColorsRef = useRef(new Map());

  // Add provider colors state
  const [providerColors, setProviderColors] = useState({});

  useEffect(() => {
    if (user && isScriptLoaded) {
      setCenter({
        lat: user.organisation.coOrdinates.latitude,
        lng: user.organisation.coOrdinates.longitude,
      });
      applyFilters();
    }
  }, [user, isScriptLoaded]);

  const onClickZoomToMarker = (lat, lng) => {
    window.scrollTo(0, 0);
    if (map) {
      map.panTo({ lat, lng });
      map.setZoom(15);
    }
  };

  const [getLocationBounds, { isLoading: locationsLoading }] =
    useGetLocationBoundsMutation();

  const [fetchPlannerPatients, { isLoading: patientsLoading }] =
    useFetchPlannerPatientsMutation();

  const [fetchPlannerProviders, { isLoading: providersLoading }] =
    useFetchPlannerProvidersMutation();

  const [fetchPlannerZones, { isLoading: zonesLoading }] =
    useFetchPlannerZonesMutation();

  const fetchLocations = async (north, south, east, west) => {
    try {
      const { data } = await getLocationBounds({
        bounds: { north, south, east, west },
        patientIds: appliedPatientsRef.current.map((patient) => patient.value),
        zoneIds: appliedZonesRef.current.map((zone) => zone.value),
        primaryProviderIds: appliedProvidersRef.current.map(
          (provider) => provider.value
        ),
      }).unwrap();

      setLocations(data);
      
      // After getting locations, fetch provider information to update colors
      const { data: providerData } = await fetchPlannerProviders({
        search: "",
        providerIds: appliedProvidersRef.current.map((provider) => provider.value),
        patientIds: appliedPatientsRef.current.map((patient) => patient.value),
        zoneIds: appliedZonesRef.current.map((zone) => zone.value),
        take: 50,
      }).unwrap();

      // Create a map of location IDs to provider colors and store provider colors
      const locationProviderMap = new Map();
      const providerColorMap = {};
      
      providerData.forEach((provider) => {
        // Use a consistent color for each provider based on their ID
        const color = randomColor({ seed: provider.id });
        providerColorMap[provider.id] = color;
        
        if (provider.visitedLocations) {
          provider.visitedLocations.forEach((locationId) => {
            if (!locationProviderMap.has(locationId)) {
              locationProviderMap.set(locationId, [{ color, providerId: provider.id }]);
            } else {
              locationProviderMap.get(locationId).push({ color, providerId: provider.id });
            }
          });
        }
      });

      setProvidersList(providerData);
      locationProviderColorsRef.current = new Map(locationProviderMap);
      setProviderColors(providerColorMap);
    } catch (error) {
      console.log("Error fetching locations:", error);
    }
  };

  const fetchPatients = async (input, initial = false) => {
    try {
      setPatients([]);

      if (!input && !initial) return;

      const { data } = await fetchPlannerPatients({
        search: input.trim(),
        patientIds: [],
        primaryProviderIds: selectedProviders.map((provider) => provider.value),
        zoneIds: selectedZones.map((zone) => zone.value),
        take: 50,
      }).unwrap();

      const patients = data.map((data) => ({
        label: data.name,
        value: data.id,
        dob: data.dob ?? "NA",
      }));

      setPatients(patients);
    } catch (error) {
      console.log("Error fetching locations:", error);
    }
  };

  const fetchProviders = async (input, initial = false) => {
    try {
      setProviders([]);

      if (!input && !initial) return;

      const { data } = await fetchPlannerProviders({
        search: input.trim(),
        providerIds: [],
        patientIds: selectedPatients.map((patient) => patient.value),
        zoneIds: selectedZones.map((zone) => zone.value),
        take: 50,
      }).unwrap();

      const providers = data.map((data) => ({
        label: data.name,
        value: data.id,
        count: data.count,
      }));

      setProviders(providers);
    } catch (error) {
      console.log("Error fetching locations:", error);
    }
  };

  const fetchZones = async (input, initial = false) => {
    try {
      setZones([]);

      if (!input && !initial) return;

      const { data } = await fetchPlannerZones({
        search: input.trim(),
        providerIds: selectedProviders.map((provider) => provider.value),
        patientIds: selectedPatients.map((patient) => patient.value),
        zoneIds: [],
        take: 50,
      }).unwrap();

      const zones = data.map((data) => ({
        label: data.name,
        value: data.id,
      }));

      setZones(zones);
    } catch (error) {
      console.log("Error fetching locations:", error);
    }
  };

  const applyFilters = async () => {
    try {
      const { data } = await fetchPlannerProviders({
        search: "",
        providerIds: selectedProviders.map((provider) => provider.value),
        patientIds: selectedPatients.map((patient) => patient.value),
        zoneIds: selectedZones.map((zone) => zone.value),
        take: 50,
      }).unwrap();

      // Create a map of location IDs to provider colors and store provider colors
      const locationProviderMap = new Map();
      const providerColorMap = {};

      data.forEach((provider) => {
        // Use a consistent color for each provider based on their ID
        const color = randomColor({ seed: provider.id });
        providerColorMap[provider.id] = color;

        if (provider.visitedLocations) {
          provider.visitedLocations.forEach((locationId) => {
            if (!locationProviderMap.has(locationId)) {
              locationProviderMap.set(locationId, [
                { color, providerId: provider.id },
              ]);
            } else {
              locationProviderMap
                .get(locationId)
                .push({ color, providerId: provider.id });
            }
          });
        }
      });

      setProvidersList(data);
      // Force a refresh of the markers by creating a new Map instance
      locationProviderColorsRef.current = new Map(locationProviderMap);
      setProviderColors(providerColorMap);
    } catch (error) {
      console.log("Error fetching locations:", error);
    }
  };

  // Helper function to get marker color based on provider visits
  const getLocationColor = (locationId, defaultColor) => {
    const providerColors = locationProviderColorsRef.current.get(locationId);
    console.log(`Location ${locationId}:`, {
      hasProviderColors: !!providerColors,
      numProviders: providerColors?.length,
      providers: providerColors,
      resultColor:
        providerColors?.length === 1
          ? providerColors[0].color
          : providerColors?.length > 1
          ? "#000000"
          : defaultColor,
    });

    if (!providerColors || providerColors.length === 0) {
      return defaultColor; // No providers visiting this location
    }
    if (providerColors.length === 1) {
      return providerColors[0].color; // Single provider color
    }
    return "#000000"; // Multiple providers - black
  };

  const fetchPatientsDebounced = useCallback(
    debounce((inputValue, initial = false) =>
      fetchPatients(inputValue, initial)
    ),
    200
  );

  const fetchProvidersDebounced = useCallback(
    debounce((inputValue, initial = false) =>
      fetchProviders(inputValue, initial)
    ),
    200
  );

  const fetchZonesDebounced = useCallback(
    debounce((inputValue, initial = false) => fetchZones(inputValue, initial)),
    200
  );

  const fetchLocationsDebounced = useCallback(
    debounce((bounds) => {
      fetchLocations(bounds.north, bounds.south, bounds.east, bounds.west);
    }, 2000),
    []
  );

  const handleSelectedPatients = (selectedOptions) => {
    if (selectedOptions.length < 3) setSelectedPatients(selectedOptions || []);
  };

  const handleSelectedProviders = (selectedOptions) => {
    if (selectedOptions.length < 3) setSelectedProviders(selectedOptions || []);
  };

  const handleSelectedZones = (selectedOptions) => {
    if (selectedOptions.length < 3) setSelectedZones(selectedOptions || []);
  };

  const handleApplyFilters = () => {
    applyFilters();
    appliedPatientsRef.current = selectedPatients;
    appliedProvidersRef.current = selectedProviders;
    appliedZonesRef.current = selectedZones;
    setRefetchLocations(true);
  };

  const handleMarkerClick = (location) => {
    console.log(location);

    setSelectedLocation(location);
  };

  useEffect(() => {
    if (refetchLocations) {
      fetchLocations(
        prevBounds.north,
        prevBounds.south,
        prevBounds.east,
        prevBounds.west
      );

      setRefetchLocations(false);
    }
  }, [refetchLocations]);

  useEffect(() => {
    if (map && center && prevBounds) {
      fetchLocations(
        prevBounds.north,
        prevBounds.south,
        prevBounds.east,
        prevBounds.west
      );
    }
  }, [map, center]);

  useEffect(() => {
    if (map) {
      const listener = map.addListener("bounds_changed", () => {
        const bounds = map.getBounds();
        if (bounds) {
          const newBounds = {
            north: bounds.getNorthEast().lat(),
            east: bounds.getNorthEast().lng(),
            south: bounds.getSouthWest().lat(),
            west: bounds.getSouthWest().lng(),
          };

          if (
            !prevBounds ||
            Math.abs(newBounds.north - prevBounds.north) > 0.01 ||
            Math.abs(newBounds.south - prevBounds.south) > 0.01 ||
            Math.abs(newBounds.east - prevBounds.east) > 0.01 ||
            Math.abs(newBounds.west - prevBounds.west) > 0.01
          ) {
            setPrevBounds(newBounds);
            fetchLocationsDebounced(newBounds);
          }
        }
      });

      return () => window.google.maps.event.removeListener(listener);
    }
  }, [map]);

  useEffect(() => {
    if (map) {
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];

      const markers = locations.map((location) => {
        const defaultColor =
          location.type === "PROVIDER_HOME"
            ? "#9333EA"
            : location.type === "COMMUNITY_HOME"
            ? "#2563EB"
            : "#DC2626";

        const markerColor = getLocationColor(location.id, defaultColor);

        console.log(markerColor);

        const marker = new window.google.maps.Marker({
          position: {
            lat: location.coordinates.latitude,
            lng: location.coordinates.longitude,
          },
          map,
          icon: {
            ...getCustomMarkerIcon(location.type),
            url: convertReactIconToUrl(location.type, markerColor),
          },
        });

        marker.addListener("click", () => handleMarkerClick(location));

        markersRef.current.push(marker);
        return marker;
      });

      if (markerClusterRef.current) {
        markerClusterRef.current.clearMarkers();
      }

      markerClusterRef.current = new MarkerClusterer({ map, markers });
    }
  }, [map, locations, providersList, locationProviderColorsRef.current]);

  useEffect(() => {
    // Initial load of providers list
    applyFilters();
  }, []); // Run once on mount

  return (
    <div className="flex flex-col">
      <p className="p-6 m-0 text-2xl font-bold">Patient Panel Visualizer</p>
      <div className="flex flex-row w-full border-t border-primarygrayborder">
        <LeftBar
          onCLickZoomToMarker={onClickZoomToMarker}
          patients={patients}
          selectedPatients={selectedPatients}
          handleSelectedPatients={handleSelectedPatients}
          fetchPatients={fetchPatientsDebounced}
          patientsLoading={patientsLoading}
          providers={providers}
          selectedProviders={selectedProviders}
          handleSelectedProviders={handleSelectedProviders}
          fetchProviders={fetchProvidersDebounced}
          providersLoading={providersLoading}
          handleApplyFilters={handleApplyFilters}
          providersList={providersList}
          zones={zones}
          selectedZones={selectedZones}
          handleSelectedZones={handleSelectedZones}
          fetchZones={fetchZonesDebounced}
          zonesLoading={zonesLoading}
          providerColors={providerColors}
        />
        {center && isScriptLoaded && (
          <RightBar
            onCLickZoomToMarker={onClickZoomToMarker}
            selectedLocation={selectedLocation}
            setMap={setMap}
            setSelectedLocation={setSelectedLocation}
            locations={locations}
            center={center}
            selectedProviders={appliedProvidersRef.current.map(
              (provider) => provider
            )}
            selectedPatients={appliedPatientsRef.current.map(
              (patient) => patient
            )}
            selectedZones={appliedZonesRef.current.map((zone) => zone)}
            locationsLoading={locationsLoading}
          />
        )}
      </div>
    </div>
  );
};

export default PatientPlannerVisualizer;
